/**
 * Central Session Client
 *
 * Handles cookie-based session API calls to Central for workspace operations.
 * All calls are proxied through the backend at /api/v1/central/session/*.
 *
 * SUITE STANDARD:
 * - Workspace list: GET /api/v1/central/session/workspaces (cookie auth, proxied)
 * - Workspace select: POST /api/v1/central/session/select-workspace (cookie auth, proxied)
 * - After selecting, must re-run OIDC authorize+token to get new access token
 *
 * CRITICAL: All fetches use `credentials: 'include'` to send cookies.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL;

/**
 * Workspace returned from Central session endpoint.
 */
export interface SessionWorkspace {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  scopes: string[];
}

/**
 * Response from GET /api/v1/central/session/workspaces
 */
export interface WorkspacesResponse {
  ok: boolean;
  workspaces?: SessionWorkspace[];
  error?: string;
}

/**
 * Response from POST /api/v1/central/session/select-workspace
 */
export interface SelectWorkspaceResponse {
  ok: boolean;
  error?: string;
}

/**
 * Result from fetching workspaces via Central session.
 */
export interface SessionWorkspaceListResult {
  hasSession: boolean;
  workspaces: SessionWorkspace[];
  error?: string;
}

/**
 * Fetches the list of workspaces from Central via backend proxy.
 *
 * @returns Workspace list result with session status
 */
export async function fetchSessionWorkspaces(): Promise<SessionWorkspaceListResult> {
  if (!API_BASE_URL) {
    return {
      hasSession: false,
      workspaces: [],
      error: 'Backend API URL not configured',
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/central/session/workspaces`, {
      method: 'GET',
      credentials: 'include', // Send cookies to backend (forwarded to Central)
      headers: {
        Accept: 'application/json',
      },
    });

    // 401/403 means no valid session
    if (response.status === 401 || response.status === 403) {
      return {
        hasSession: false,
        workspaces: [],
        error: 'No Central session',
      };
    }

    // 404 means endpoint not implemented
    if (response.status === 404) {
      return {
        hasSession: false,
        workspaces: [],
        error: 'Workspace switching not available',
      };
    }

    if (!response.ok) {
      return {
        hasSession: false,
        workspaces: [],
        error: `Backend returned ${response.status}`,
      };
    }

    const data: WorkspacesResponse = await response.json();

    if (!data.ok) {
      return {
        hasSession: false,
        workspaces: [],
        error: data.error || 'Unknown error',
      };
    }

    return {
      hasSession: true,
      workspaces: data.workspaces ?? [],
    };
  } catch (err) {
    console.error('Failed to fetch workspaces:', err);
    return {
      hasSession: false,
      workspaces: [],
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}

/**
 * Selects a workspace in Central's session via backend proxy.
 *
 * After this succeeds, caller MUST run OIDC authorize+token to get new access token.
 *
 * @param workspaceId - The workspace to select
 * @returns Success/failure result
 */
export async function selectSessionWorkspace(
  workspaceId: string
): Promise<{ ok: boolean; error?: string }> {
  if (!API_BASE_URL) {
    return { ok: false, error: 'Backend API URL not configured' };
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/central/session/select-workspace`,
      {
        method: 'POST',
        credentials: 'include', // Send cookies to backend (forwarded to Central)
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ workspaceId }),
      }
    );

    // 401/403 means no valid session
    if (response.status === 401 || response.status === 403) {
      return { ok: false, error: 'No Central session - please log in again' };
    }

    // 404 means endpoint not implemented
    if (response.status === 404) {
      return { ok: false, error: 'Workspace switching not available' };
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        error: errorData.error || `Backend returned ${response.status}`,
      };
    }

    const data: SelectWorkspaceResponse = await response.json();

    if (!data.ok) {
      return { ok: false, error: data.error || 'Unknown error' };
    }

    return { ok: true };
  } catch (err) {
    console.error('Failed to select workspace:', err);
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}
